/* Hilfen für DateField.vue (Textfeld + Kalenderfenster) — reine Funktionen, kein DOM.
 *
 * Zeitzonen: Vuetify arbeitet im Kalender mit Date-Objekten in LOKALER Zeit. Ein ISO-Text "2008-03-15" per
 * new Date(text) wäre UTC-Mitternacht und in Zeitzonen westlich von UTC der 14. März. Deshalb gehen alle Umrechnungen
 * über die Bestandteile Jahr, Monat, Tag: ISO → new Date(j, m-1, t, 12) (Mittag, damit auch eine Zeitumstellung um
 * Mitternacht den Tag nicht verschiebt) und Date → getFullYear/getMonth/getDate. Nie toISOString, nie UTC.
 * Tagesrechnung (Pfeiltasten) läuft rein über Date.UTC, also ganz ohne Ortszeit.
 */

import { isRealDate, daysInMonth } from './dateMask.js';
import { todayIso } from './birthDate.js';

/** Technische Grenzen, wenn ein Feld keine eigenen hat (Auftrag F9). */
export const DATE_MIN = '1950-01-01';
export const DATE_MAX = '2100-12-31';

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;
const pad = (n) => String(n).padStart(2, '0');

/** "JJJJ-MM-TT" → [j, m, t] oder null (auch bei nicht vorhandenem Kalendertag). */
export function parseIso(iso) {
  const m = ISO.exec(String(iso ?? '').trim());
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  return isRealDate(y, mo, d) ? [y, mo, d] : null;
}

export function partsToIso(y, m, d) {
  return `${String(y).padStart(4, '0')}-${pad(m)}-${pad(d)}`;
}

/** ISO → Date in lokaler Zeit (Mittag des Tages); null bei ungültigem Text. */
export function isoToLocalDate(iso) {
  const p = parseIso(iso);
  return p ? new Date(p[0], p[1] - 1, p[2], 12, 0, 0) : null;
}

/** Date (lokale Zeit) → "JJJJ-MM-TT" über die lokalen Bestandteile; null bei ungültigem Date. */
export function localDateToIso(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return partsToIso(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

/**
 * Prüfung eines ISO-Werts gegen die Grenzen.
 * @returns {'' | 'invalid' | 'tooEarly' | 'tooLate'}  '' = in Ordnung
 */
export function classifyIso(iso, { min = DATE_MIN, max = DATE_MAX } = {}) {
  if (!parseIso(iso)) return 'invalid';
  if (min && iso < min) return 'tooEarly';
  if (max && iso > max) return 'tooLate';
  return '';
}

/** Liegt der Tag im erlaubten Bereich? (Kalender: andere Tage sind deaktiviert) */
export function isAllowedIso(iso, limits) {
  return classifyIso(iso, limits) === '';
}

/** Tage verschieben (Pfeiltasten); rein über UTC-Tageszahlen, daher unabhängig von Zeitzone und Zeitumstellung. */
export function addDaysIso(iso, delta) {
  const p = parseIso(iso);
  if (!p) return null;
  const t = new Date(Date.UTC(p[0], p[1] - 1, p[2] + delta));
  return partsToIso(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate());
}

/** Monate verschieben (Bild hoch/runter); ein zu großer Tag rückt auf den Monatsletzten (31.01. + 1 → 28./29.02.). */
export function addMonthsIso(iso, delta) {
  const p = parseIso(iso);
  if (!p) return null;
  const idx = p[0] * 12 + (p[1] - 1) + delta;
  const y = Math.floor(idx / 12);
  const m = (idx % 12 + 12) % 12 + 1;
  return partsToIso(y, m, Math.min(p[2], daysInMonth(y, m)));
}

/** Ins erlaubte Intervall schieben. */
export function clampIso(iso, { min = DATE_MIN, max = DATE_MAX } = {}) {
  if (min && iso < min) return min;
  if (max && iso > max) return max;
  return iso;
}

/**
 * Wo öffnet der Kalender? Mit Wert: dort. Leer: im Jahr startYear (z. B. Geburtsdaten: ca. 16 Jahre vor heute), Januar;
 * ohne startYear im aktuellen Monat. Immer innerhalb der Grenzen.
 * @returns {{ year: number, month: number }}  month 0-basiert (wie Vuetify)
 */
export function calendarStart({ iso = '', startYear = null, min = DATE_MIN, max = DATE_MAX, today = todayIso() } = {}) {
  let at;
  if (parseIso(iso)) at = iso;
  else if (Number.isInteger(startYear)) at = partsToIso(startYear, 1, 1);
  else at = today;
  const c = clampIso(at, { min, max });
  const p = parseIso(c) || parseIso(today);
  return { year: p[0], month: p[1] - 1 };
}

/** Vorschlag für startYear bei Geburtsdaten: heute minus 16 Jahre. */
export function birthStartYear(today = todayIso()) {
  return Number(today.slice(0, 4)) - 16;
}
